<template>
  <v-form ref="form" @submit.prevent="createOrg">

    <error-dialog :status="loadAccountStatus" />
    <error-dialog :status="createOrgStatus" />

    <sign-in-dialog v-model="showSignIn">
      <template v-slot:subtitle>Sign in to verify your email address</template>
    </sign-in-dialog>

    <v-text-field
      v-if="!hideEmail"
      class="mb-3"
      :value="isSignedIn ? global.profile.email : null"
      name="Email address" label="Email address" readonly
      :error-messages="accountErr"
      :rules="[ v => !!v || 'Required' ]"
      @click="showSignIn = true"
    >
      <template v-slot:append-outer>
        <v-btn
          small
          :loading="loadAccountStatus.pending"
          @click="showSignIn = true"
          v-text="isSignedIn ? 'Change…' : 'Sign in…'"
        />
      </template>
    </v-text-field>
    <v-row>
      <v-col cols="12">
        <v-text-field
          outlined dense hide-details name="Organization name" label="Organization name"
          :rules="[ v => !!v || 'Required' ]"
          v-model="params.org_name"
        />
      </v-col>
      <v-col cols="12">
        <v-text-field
          outlined dense hide-details name="Name" label="Your name"
          :rules="[ v => !!v || 'Required' ]"
          v-model="params.customer_name"
        />
      </v-col>
      <v-col cols="12">
        <div class="mt-6 title" style="color: rgba(0, 0, 0, 0.87);">What do you want to use Ellacard to do?</div>
        <div class="subtitle">We'll send you tutorials / examples related to your selection(s)</div>
        <!-- NOTE: make sure that if you add or change the values of these checkbox that you also add the corresponding values to the 'Signup use cases' field in Pipedrive: https://ellacard.pipedrive.com/settings/fields?type=ORGANIZATION -->
        <!--
        <v-checkbox
          hide-details label="Send ad-hoc cards / group cards" value="AD_HOC"
          :rules="[ v => !!params.use_cases.length ]"
          v-model="params.use_cases"
        />
        -->
        <v-checkbox
          hide-details label="Send internally to your organization's employees" value="INTERNAL"
          :rules="[ v => !!params.use_cases.length ]"
          v-model="params.use_cases"
        />
        <v-checkbox
          hide-details label="Send outside of your organization" value="EXTERNAL"
          :rules="[ v => !!params.use_cases.length ]"
          v-model="params.use_cases"
        />
        <div class="text--label-active pl-8">e.g. to clients / donors</div>
        <v-checkbox
          hide-details label="Add custom logo / branding" value="BRANDING"
          :rules="[ v => !!params.use_cases.length ]"
          v-model="params.use_cases"
        />
        <div class="text--label-active pl-8">And set a custom sender email address</div>
        <!--
        <v-checkbox
          hide-details label="Create custom card designs" value="DESIGNS"
          :rules="[ v => !!params.use_cases.length ]"
          v-model="params.use_cases"
        />
        -->
        <v-checkbox
          hide-details label="Automate cards / group cards" value="AUTOMATION"
          :rules="[ v => !!params.use_cases.length ]"
          v-model="params.use_cases"
        />
        <div class="text--label-active pl-8">e.g. for client birthdays or employee work anniversaries</div>
        <v-checkbox
          hide-details label="Send gift cards" value="GIFT_CARDS"
          :rules="[ v => !!params.use_cases.length ]"
          v-model="params.use_cases"
        />
        <div class="text--label-active pl-8">e.g. Amazon, Starbucks, Uber, Visa…</div>
        <!--
        <v-checkbox
          hide-details label="Send event invitations" value="INVITATIONS"
          :rules="[ v => !!params.use_cases.length ]"
          v-model="params.use_cases"
        />
        -->
        <v-checkbox
          hide-details label="Share access with other members of my organization" value="SHARED_ACCESS"
          :rules="[ v => !!params.use_cases.length ]"
          v-model="params.use_cases"
        />
        <div class="text--label-active pl-8">With detailed usage metrics and administrative oversight</div>
        <!--
        <v-checkbox
          hide-details label="Send cards / custom emails to a mailing list of 500+" value="BULK_EMAIL"
          :rules="[ v => !!params.use_cases.length ]"
          v-model="params.use_cases"
        />
        <div class="text--label-active pl-8">With advanced analytics / tracking</div>
        -->
        <v-checkbox
          hide-details label="Other…" value="OTHER"
          :rules="[ v => !!params.use_cases.length ]"
          v-model="params.use_cases"
        />
        <v-expand-transition>
          <v-textarea
            v-show="params.use_cases.includes('OTHER')"
            class="mt-1 pl-8"
            hide-details dense outlined auto-grow rows="2"
            :rules="[ v => !params.use_cases.includes('OTHER') || !!v ]"
            v-model="params.notes"
          />
        </v-expand-transition>
      </v-col>
    </v-row>

    <div v-if="!hideBtns" class="mt-6 d-flex">
      <v-spacer />
      <v-btn v-if="!hideCancel" class="mr-4" @click="$emit('cancel')">Cancel</v-btn>
      <v-btn color="primary" type="Submit" :disabled="isDisabled" :loading="createOrgStatus.pending">
        Submit
      </v-btn>
    </div>
  </v-form>
</template>


<script>
import SignInDialog from '@/components/SignInDialog.vue';
import { OPError } from '@/utils/op-dev-fetch';


export default {
  name: 'org-sign-up-form',

  components: {
    SignInDialog
  },

  props: {
    hideCancel: Boolean,
    hideEmail: Boolean,
    hideBtns: Boolean
  },

  data() {
    return {
      account: null,
      showSignIn: false,
      params: {
        org_name: null,
        customer_name: null,
        use_cases: [],
        notes: null
      },
      loadAccountStatus: this.$track('loadAccount'),
      createOrgStatus: this.$track('createOrg')
    };
  },

  computed: {
    accountErr() {
      if (!this.isSignedIn)
        return 'Sign in to verify your email address';
      if (this.global.profile.org)
        return 'This account already belongs to an organization';
      if ((this.account?.subscription?.metadata.code == 'SINGLE-USER') && this.account?.subscription.next_at)
        // Note that this also catches the case where a user has signed up for but cancelled their single-user
        // subscription
        return 'Access to org tools is not allowed with a SINGLE-USER subscription';
      return null;
    },

    isDisabled() {
      return !!this.accountErr || this.loadAccountStatus.pending;
    }
  },

  methods: {
    getOrgParams() {
      // Warning: this is called externally from components like the PaymentDialog
      return this.$refs.form.validate() ? this.params : null;
    },

    async loadAccount() {
      this.account = await this.$call('get-account');
    },

    async createOrg() {
      if (!this.$refs.form.validate())
        throw new VisibleError('Please fix the highlighted errors before continuing');

      if (this.isDisabled)
        return;

      try {
        await this.$call('create-org', { ...this.params, url: location.href });
        await this.$refreshJWT();
        this.$emit('complete');
      } catch (e) {
        if (e instanceof OPError && e.message == 'EXISTING_ORG')
          throw new VisibleError('This account is already part of an Ellacard organization');
        if (e instanceof OPError && e.message == 'EXISTING_SUBSCRIPTION')
          throw new VisibleError('This account is already subscribed as an individual and cannot be part of an Ellacard organization – please contact us to upgrade your subscription: <a href="mailto:contact@ellacard.com">contact@ellacard.com</a>');
        throw e;
      }
    }
  },

  watch: {
    globalUserId: {
      immediate: true,
      handler(v) {
        this.account = null;
        v && this.loadAccount();
      }
    }
  }
}
</script>
